import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useSbCalls } from "@/lib/sendbird-calls";
import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { GroupCall } from "@/components/group-call";
import { useToast } from "@/components/ui/use-toast";

export const Route = createFileRoute("/_authenticated/calls/$roomId")({
  component: () => <CallRoom />,
});

function CallRoom() {
  const sbCalls = useSbCalls();
  const navigate = useNavigate();
  const [initiating, setInitiating] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { roomId } = useParams({ strict: false }) as { roomId: string };
  const { rooms } = sbCalls;
  const { toast } = useToast();

  useEffect(() => {
    if (!roomId) {
      setError("No room ID provided");
      return;
    }

    const timer = setTimeout(() => {
      (async () => {
        try {
          const room = await sbCalls.fetchRoomById(roomId);

          if (!room) {
            setError("Room not found");
            return;
          }

          await room.enter({
            audioEnabled: true,
            videoEnabled: true,
            kickSiblings: true,
          });
          setInitiating(false);
        } catch (err) {
          setError(
            err instanceof Error ? err.message : "Failed to join the room",
          );
        }
      })();
    }, 2000);

    return () => clearTimeout(timer);
  }, [roomId]);

  const onCall = useMemo(() => {
    return rooms.find((r) => !!r.localParticipant);
  }, [rooms]);

  // Navigate back to dashboard if there's an error or no room found
  useEffect(() => {
    if (error || (!initiating && !onCall)) {
      const timer = setTimeout(() => {
        navigate({ to: "/conversations" });
        toast({
          title: "Could not join the call",
          description: "Please try again",
          variant: "destructive",
        });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, initiating, onCall, navigate, toast]);

  if (initiating) {
    return (
      <div className="h-screen-nav flex h-screen w-full items-center justify-center bg-primary-100">
        <Loader2 className="h-12 w-12 animate-spin text-blue-brand" />
      </div>
    );
  }

  if (error || !onCall) {
    return (
      <div className="h-screen-nav flex h-screen w-full items-center justify-center bg-primary-100">
        <div className="flex flex-col items-center gap-2 text-secondary">
          <div className="text-sm">{error || "No call found"}</div>
          <div className="text-sm">Redirecting to dashboard...</div>
        </div>
      </div>
    );
  }

  return <GroupCall room={onCall} />;
}
